'use client';

import { Typography, type TypographyProps } from '@mui/material';
import { useLocale, useTranslations } from 'next-intl';

import type { PriceUnit } from '@/app/api/lib/products/product.pricing';
import { displayableAmount, formatPrice } from '@/components/common/price-format';
import { useCurrency } from '@/configurations/providers/CurrencyProvider';

/**
 * Affiche « À partir de {montant} {€|$} par {unité} » pour le prix role-gated
 * d'un Produit (carte, ligne, ou fiche). Le montant suit la devise choisie dans
 * la navbar. Ne rend rien si le prix est absent (Visiteur / pas de tarif) ou si
 * la devise sélectionnée n'a pas de montant. Un forfait (unité `null`) rend
 * « À partir de {montant} » sans « par … ».
 */

type PriceLike = {
    amountEur: number | null;
    amountUsd: number | null;
    priceUnit: PriceUnit | null;
};

type ProductPriceLabelProps = {
    price: PriceLike | null | undefined;
    variant?: TypographyProps['variant'];
    color?: TypographyProps['color'];
    'data-testid'?: string;
};

export function ProductPriceLabel({
    price,
    variant = 'subtitle2',
    color = 'text.primary',
    'data-testid': dataTestId = 'product-price',
}: ProductPriceLabelProps) {
    const { currency } = useCurrency();
    const t = useTranslations('productPrice');
    const locale = useLocale();

    if (!price) return null;
    // `displayableAmount` neutralise aussi le 0, par lequel la source encode
    // parfois un prix inconnu : pas de montant → pas de bloc prix.
    const amount = displayableAmount(currency === 'EUR' ? price.amountEur : price.amountUsd);
    if (amount === null) return null;

    const amountLabel = formatPrice(amount, currency, locale);
    const label = price.priceUnit
        ? t('fromPerUnit', { amount: amountLabel, unit: t(`units.${price.priceUnit}`) })
        : t('from', { amount: amountLabel });

    return (
        <Typography
            variant={variant}
            component="p"
            color={color}
            sx={{ fontWeight: 600 }}
            data-testid={dataTestId}
        >
            {label}
        </Typography>
    );
}
